import { Dialog, DialogContent } from '@mui/material';
import { useFormik } from 'formik';
import { t } from 'i18next';
import { useSelector } from 'react-redux';
import * as Yup from 'yup';
import { MainButton } from '../../components/Buttons/MainButton';
import TextFieldFormik from '../../components/FormikForm/TextFieldFormik';
import {
  simpleModalActionsContainerStyles,
  simpleModalPaperDynamicSizeRootStyles,
  simpleModalPaperFixedWidthStyles,
} from '../../components/Modals/styles/simpleModalStyles';
import { useUpdateUserName } from '../../graphql/apis/user/mutations/hooks/useUpdateUserName';
import { RootState } from '../../redux/store';
import { colors } from '../../theme/colors';
import spacing from '../../theme/spacing';
import { fontSize } from '../../theme/typography';
import { sanitizeName } from '../LoginPage/AuthContent/utils';
import { useState } from 'react';
import { ERROR_MESSAGE_STATUS } from '../../graphql/responseErrorMessages';
import LabelText from '../../components/FormikForm/LabelText';

interface NameEditModalProps {
  isOpen: boolean;
  handleClose: () => void;
}

const NameEditModal = ({ isOpen, handleClose }: NameEditModalProps) => {
  const currentName = useSelector((state: RootState) => state.user?.name || '');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const { updateUserName, loading } = useUpdateUserName({
    onCompleted: (data) => {
      const isSuccess = data?.updateUserName?.success;
      const errorMessageStatus = data?.updateUserName?.errorMessageStatus;
      if (isSuccess) {
        handleClose();
      }

      if (errorMessageStatus) {
        if (
          errorMessageStatus ===
          ERROR_MESSAGE_STATUS.ERROR_UPDATE_TOO_FREQUENTLY
        ) {
          setErrorMessage(t('accountSettings.nameChangeFrequencyError'));
        } else {
          setErrorMessage(t('common.errorOccured'));
        }
      }
    },
    onError: (error) => {
      console.error('Error updating name:', error);
      if (
        error.graphQLErrors[0]?.extensions?.code ===
        ERROR_MESSAGE_STATUS.ERROR_UPDATE_TOO_FREQUENTLY
      ) {
        setErrorMessage(t('accountSettings.nameChangeFrequencyError'));
      }
    },
  });

  const formik = useFormik({
    initialValues: {
      name: currentName,
    },
    validationSchema: Yup.object({
      name: Yup.string().required(`${t('common.required')}`),
    }),
    onSubmit: (values) => {
      updateUserName(values.name);
    },
  });

  const isNameChanged =
    formik.values.name !== currentName && formik.values.name.trim() !== '';

  return (
    <Dialog
      open={isOpen}
      onClose={handleClose}
      sx={{
        '& .MuiPaper-root': {
          ...simpleModalPaperDynamicSizeRootStyles,
          ...simpleModalPaperFixedWidthStyles,
        },
      }}
    >
      <DialogContent>
        <div
          style={{
            color: colors.textPrimary,
            fontWeight: '500',
            fontSize: fontSize.regular + 2,
            marginBottom: spacing.smallMedium,
          }}
        >
          {t('accountSettings.editName')}
        </div>
        {errorMessage && (
          <div
            style={{
              textAlign: 'center',
              marginBottom: spacing.medium,
              marginTop: spacing.medium,
            }}
          >
            <LabelText error text={errorMessage} />
          </div>
        )}
        <TextFieldFormik
          id="name"
          name="name"
          label={t('accountSettings.name')}
          value={formik.values.name}
          onChange={(e) =>
            formik.setFieldValue('name', sanitizeName(e.target.value))
          }
          error={formik.touched.name && Boolean(formik.errors.name)}
          helperText={formik.touched.name && formik.errors.name}
        />
      </DialogContent>
      <div style={simpleModalActionsContainerStyles}>
        <MainButton
          onClick={handleClose}
          style={{
            color: colors.textPrimary,
            fontWeight: 'normal',
          }}
          disabled={loading}
          typeStyle="link"
          text={t('common.close')}
        />
        <MainButton
          onClick={() => formik.handleSubmit()}
          typeStyle="link"
          text={t('common.save')}
          style={{ color: colors.primary }}
          disabled={loading || !isNameChanged}
        />
      </div>
    </Dialog>
  );
};

export default NameEditModal;
